import { useState, useEffect } from 'react';
import { sessionAPI, profileAPI } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { useSocket } from '../contexts/SocketContext';
import './PlayerList.css';

export default function PlayerList({
  gameId,
  participants = [],
  hostId,
  turnState,
  isHost,
  onKickPlayer,
  onBanPlayer,
  onViewProfile
}) {
  const { user } = useAuth();
  const { typingUsers } = useSocket();
  const [expanded, setExpanded] = useState(false);

  // Get the typing status for a user
  const isTyping = (userId) => {
    return typingUsers.some(u => u.userId === userId);
  };

  // Check if it's a user's turn
  const isUsersTurn = (userId) => {
    return turnState?.currentUserId === userId;
  };

  // Format turn timer
  const formatTimeLeft = () => {
    if (!turnState?.turnEndTime) return null;

    const now = new Date();
    const endTime = new Date(turnState.turnEndTime);
    const diff = Math.max(0, Math.floor((endTime - now) / 1000));

    const minutes = Math.floor(diff / 60);
    const seconds = diff % 60;

    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Get initials from name
  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Active participants only
  const activeParticipants = participants.filter(p => p.isActive);

  if (activeParticipants.length === 0) {
    return null;
  }

  return (
    <div className={`player-list ${expanded ? 'expanded' : ''}`}>
      <button
        className="player-list-toggle"
        onClick={() => setExpanded(!expanded)}
        title={expanded ? 'Collapse player list' : 'Expand player list'}
      >
        <span className="player-count">{activeParticipants.length}</span>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
          <circle cx="9" cy="7" r="4"></circle>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
        </svg>
        <span className={`expand-icon ${expanded ? 'rotated' : ''}`}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </span>
      </button>

      {expanded && (
        <div className="player-list-content">
          <div className="player-list-header">
            <h4>Players</h4>
            {turnState?.currentUserId && (
              <span className="turn-timer">{formatTimeLeft()}</span>
            )}
          </div>

          <div className="players">
            {activeParticipants.map((participant) => {
              const isCurrentUser = participant.user?._id === user?._id ||
                                   participant.user === user?._id;
              const isParticipantHost = participant.user?._id === hostId ||
                                        participant.user === hostId;
              const hasTurn = isUsersTurn(participant.user?._id || participant.user);
              const typing = isTyping(participant.user?._id || participant.user);

              return (
                <div
                  key={participant.user?._id || participant.user}
                  className={`player-item ${hasTurn ? 'has-turn' : ''} ${isCurrentUser ? 'is-you' : ''}`}
                >
                  <div className="player-avatar">
                    {participant.user?.profile?.profilePhoto ? (
                      <img
                        src={participant.user.profile.profilePhoto}
                        alt={participant.username}
                        className="avatar-img"
                      />
                    ) : (
                      <span className="avatar-initials">
                        {getInitials(participant.characterName || participant.username)}
                      </span>
                    )}
                    <span className={`online-indicator ${participant.user?.isOnline ? 'online' : 'offline'}`} />
                  </div>

                  <div className="player-info">
                    <div className="player-name">
                      <span
                        className="username"
                        onClick={() => onViewProfile?.(participant.user?._id || participant.user)}
                      >
                        {participant.username}
                        {isCurrentUser && <span className="you-badge">(You)</span>}
                      </span>
                      {isParticipantHost && (
                        <span className="host-icon" title="Host">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/>
                          </svg>
                        </span>
                      )}
                    </div>
                    <div className="character-name">
                      {participant.characterName}
                      {participant.character?.level && ` • Lv${participant.character.level}`}
                    </div>
                    {typing && (
                      <div className="typing-indicator">
                        <span className="typing-dot"></span>
                        <span className="typing-dot"></span>
                        <span className="typing-dot"></span>
                      </div>
                    )}
                  </div>

                  <div className="player-actions">
                    {hasTurn && (
                      <span className="turn-badge">Turn</span>
                    )}
                    {participant.afkCount > 0 && (
                      <span className="afk-badge" title={`AFK warnings: ${participant.afkCount}/3`}>
                        AFK {participant.afkCount}
                      </span>
                    )}
                    {isHost && !isCurrentUser && !isParticipantHost && (
                      <>
                        <button
                          className="kick-btn"
                          onClick={() => onKickPlayer?.(participant.user?._id || participant.user)}
                          title="Remove player"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                          </svg>
                        </button>
                        <button
                          className="ban-btn"
                          onClick={() => onBanPlayer?.(participant.user?._id || participant.user)}
                          title="Ban player from session"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line>
                          </svg>
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
